import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, App, Button, Card, Col, Empty, Input, InputNumber, Modal, Row, Segmented, Select, Space, Steps, Table, Typography } from "antd";
import {
  arrivalProgress,
  closeArrival,
  closePrintTask,
  downloadFile,
  getArrival,
  getPrintTask,
  listArrivals,
  listPrintLogs,
  listPrintTasks,
  lockArrival,
  requestVoid,
  type ArrivalProgress,
  type ArrivalSummary,
  type PrintLogRow,
  type PrintTask,
} from "../api/client";
import { BarcodeSvg } from "../components/BarcodeSvg";
import { AdminScanNotice, formatWhen, LoadFail, PageSpin, ScanBox } from "../components/widgets";
import { submitArrivalScan, submitTaskScan } from "../scan/actions";

function lockCount(quantities: number[], spareRatio: number): number {
  return quantities.reduce((sum, qty) => sum + Math.ceil((qty * (100 + spareRatio)) / 100), 0);
}

export function TagStation(): JSX.Element {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [arrivals, setArrivals] = useState<ArrivalSummary[]>([]);
  const [arrivalNo, setArrivalNo] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<number[]>([]);
  const [progress, setProgress] = useState<ArrivalProgress | null>(null);
  const [progressPage, setProgressPage] = useState(1);
  const [logs, setLogs] = useState<PrintLogRow[]>([]);
  const [legacy, setLegacy] = useState<PrintTask[]>([]);
  const [legacyId, setLegacyId] = useState<string | null>(null);
  const [legacyTask, setLegacyTask] = useState<PrintTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [face, setFace] = useState<"hangtag" | "peel">("hangtag");
  const [spareRatio, setSpareRatio] = useState(15);
  const [activated, setActivated] = useState<{ humanCode: string; serial: string; batchNo: string; status: string } | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [voidSerial, setVoidSerial] = useState("");
  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState("");

  const loadLists = useCallback(() => {
    setLoading(true);
    void Promise.all([listArrivals(1, 50, true), listPrintTasks(true), listPrintLogs()])
      .then(([arrivalPage, tasks, nextLogs]) => {
        setArrivals(arrivalPage.rows);
        setLegacy(tasks);
        setLogs(nextLogs);
        setFailed(false);
        setArrivalNo((current) => current ?? arrivalPage.rows[0]?.arrivalNo ?? null);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  const loadProgress = useCallback(() => {
    if (!arrivalNo) {
      setProgress(null);
      setQuantities([]);
      return;
    }
    void Promise.all([
      getArrival(arrivalNo, 1, 50, true),
      arrivalProgress(arrivalNo, progressPage, 50),
    ]).then(([detail, nextProgress]) => {
      setQuantities(detail.plannedQtys);
      setProgress(nextProgress);
    });
  }, [arrivalNo, progressPage]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const lockedNumbers = useMemo(() => lockCount(quantities, spareRatio), [quantities, spareRatio]);
  const selected = arrivals.find((item) => item.arrivalNo === arrivalNo) ?? null;
  const status = progress?.status ?? selected?.status ?? "OPEN";

  if (loading && arrivals.length === 0 && legacy.length === 0) return <PageSpin tip="正在读取到货单…" />;
  if (failed) return <LoadFail title="这一步没有完成。" onRetry={loadLists} />;

  const step = !selected ? 0 : status === "CLOSED" || (progress?.remaining === 0 && status !== "OPEN") ? 4 : (progress?.activated ?? 0) > 0 ? 3 : status === "OPEN" ? 0 : 2;

  return (
    <>
      <AdminScanNotice />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="打出来还不等于入库"
        description="吊牌和外标可以先打、可以打坏。只有在下面扫过的那一双，才算入库。打坏的号作废后不能再用，要补一个新号。"
      />
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 16 }}
        items={[
          { title: "选这张到货单" },
          { title: "锁定全部号段" },
          { title: "打印" },
          { title: "逐双扫码入库" },
          { title: "三数对平" },
        ]}
      />
      <Card title="锁定这张到货单" className="no-print" style={{ marginBottom: 16 }}>
        {arrivals.length === 0 ? (
          <Empty description="还没有可打印的到货单。请先在 SKU 建档导入到货清单。" />
        ) : (
          <Space wrap>
            <Select
              style={{ width: 360 }}
              placeholder="选择到货单"
              value={arrivalNo ?? undefined}
              onChange={(value) => {
                setArrivalNo(value);
                setProgressPage(1);
                setActivated(null);
              }}
              options={arrivals.map((item) => ({
                value: item.arrivalNo,
                label: `${item.arrivalNo} · ${item.lineCount} 个 SKU · ${item.plannedQty} 双`,
              }))}
            />
            <span>多备比例</span>
            <InputNumber
              min={10}
              max={20}
              value={spareRatio}
              addonAfter="%"
              disabled={status !== "OPEN"}
              onChange={(value) => setSpareRatio(typeof value === "number" ? value : 15)}
            />
            <Typography.Text>将为这张到货单锁定 {lockedNumbers} 个号</Typography.Text>
            <Button
              type="primary"
              disabled={!arrivalNo || status !== "OPEN"}
              onClick={() => {
                if (!arrivalNo) return;
                void lockArrival(arrivalNo, spareRatio)
                  .then(() => {
                    message.success("号段已锁定。接下来下载吊牌 PDF 和外标 PDF，拿到标签机上再打。");
                    loadLists();
                    loadProgress();
                  })
                  .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这一步没有完成。"));
              }}
            >
              锁定这张到货单的全部号段
            </Button>
          </Space>
        )}
      </Card>
      {selected && status !== "OPEN" ? (
        <>
          <Space className="no-print" wrap style={{ marginBottom: 12 }}>
            <Segmented
              value={face}
              onChange={(value) => {
                if (value === "hangtag" || value === "peel") setFace(value);
              }}
              options={[
                { label: "鞋身吊牌 50×90mm", value: "hangtag" },
                { label: "鞋盒外标 40×30mm", value: "peel" },
              ]}
            />
            <Button onClick={() => downloadArrival(selected.arrivalNo, "hangtag", message, loadLists)}>
              下载这张到货单的吊牌 PDF
            </Button>
            <Button onClick={() => downloadArrival(selected.arrivalNo, "peel", message, loadLists)}>
              下载这张到货单的外标 PDF
            </Button>
            <Button onClick={() => window.print()}>只打印当前这种预览</Button>
          </Space>
          <Alert
            className="no-print"
            type="warning"
            showIcon
            message="屏幕只预览前 24 张。全部标签请下载 PDF，拿到标签机上再打。"
          />
          <Row gutter={[12, 12]} className="print-area" style={{ marginTop: 16 }}>
            {(progress?.preview ?? []).map((unit) => (
              <Col key={unit.serial} xs={24} sm={12} md={8}>
                <LabelCard face={face} humanCode={unit.humanCode} internalCode={unit.internalCode} serial={unit.serial} gs1={unit.gs1} />
              </Col>
            ))}
          </Row>
          <Card className="no-print" title="扫码后这双才入库" style={{ marginTop: 16 }}>
            <Button type="link" style={{ paddingLeft: 0 }} onClick={() => navigate("/scan")}>
              去扫码工作台逐双入库
            </Button>
            <ScanBox
              placeholder="扫描这双的外标或吊牌"
              onSubmit={async (code) => {
                setActivateError(null);
                const result = await submitArrivalScan(selected.arrivalNo, code);
                if (!result.ok) {
                  setActivateError(result.message);
                  return;
                }
                setActivated({
                  humanCode: result.humanCode,
                  serial: result.serial,
                  batchNo: result.batchNo,
                  status: result.status,
                });
                message.success(result.message);
                loadProgress();
              }}
            />
            {activateError ? <Alert type="error" showIcon message={activateError} /> : null}
            {activated ? (
              <p>
                {activated.humanCode} · {activated.serial} · {activated.batchNo} · 待上架
              </p>
            ) : null}
          </Card>
          <Card className="no-print" title="这张到货单的进度" style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 12 }}>
              <Input
                style={{ width: 220 }}
                placeholder="坏标的序列号"
                value={voidSerial}
                onChange={(event) => setVoidSerial(event.target.value)}
              />
              <Button
                disabled={!/^\d{8}$/.test(voidSerial.trim())}
                onClick={() => {
                  setReason("");
                  setVoidOpen(true);
                }}
              >
                把这个号提交作废申请
              </Button>
            </Space>
            <Table
              rowKey="humanCode"
              dataSource={progress?.lines ?? []}
              pagination={{
                current: progressPage,
                pageSize: 50,
                total: progress?.lineTotal ?? 0,
                onChange: setProgressPage,
              }}
              columns={[
                { title: "SKU", dataIndex: "humanCode" },
                { title: "计划双数", dataIndex: "plannedQty" },
                { title: "已入库", dataIndex: "activated" },
                { title: "已作废", dataIndex: "voided" },
                { title: "待入库", dataIndex: "remaining" },
              ]}
            />
            {(progress?.remaining ?? 1) === 0 ? (
              <Alert type="success" showIcon message="三数已经对齐，这张到货单可以收工。" />
            ) : (
              <Alert
                type="error"
                showIcon
                message={`还有 ${progress?.remaining ?? 0} 张待入库。打印 ${progress?.printed ?? 0}，已入库 ${progress?.activated ?? 0}，作废 ${progress?.voided ?? 0}。`}
              />
            )}
            <Button
              style={{ marginTop: 12 }}
              disabled={(progress?.remaining ?? 1) > 0 || status === "CLOSED"}
              onClick={() => {
                void closeArrival(selected.arrivalNo)
                  .then(() => {
                    message.success("这张到货单已收工。");
                    loadLists();
                    loadProgress();
                  })
                  .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这一步没有完成。"));
              }}
            >
              确认这张到货单三数对齐并结束
            </Button>
          </Card>
        </>
      ) : null}
      <Card className="no-print" title="更早的单行任务" style={{ marginTop: 16 }}>
        {legacy.length === 0 ? (
          <Empty description="没有更早的单行任务。" />
        ) : (
          <LegacyTasks
            tasks={legacy}
            selectedId={legacyId}
            task={legacyTask}
            onSelect={(id) => {
              setLegacyId(id);
              void getPrintTask(id).then(setLegacyTask);
            }}
            onActivated={() => {
              if (!legacyId) return;
              void getPrintTask(legacyId).then(setLegacyTask);
            }}
            reload={loadLists}
          />
        )}
      </Card>
      <Card className="no-print" title="打印记录" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          dataSource={logs}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "还没有打印记录。锁定号段并下载 PDF 后，这里会留下谁打过。" }}
          columns={[
            { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
            { title: "操作人", dataIndex: "operatorName" },
            { title: "类型", dataIndex: "template", render: (value: string) => templateLabel(value) },
            { title: "对象", dataIndex: "objectRef" },
            { title: "号段或编码", dataIndex: "rangeText" },
            { title: "张数", dataIndex: "copies" },
            { title: "任务号", dataIndex: "taskNo", render: (value: string | null) => value ?? "—" },
          ]}
        />
      </Card>
      <Modal
        destroyOnClose
        open={voidOpen}
        title="提交作废申请"
        okText="把这个号提交作废申请"
        cancelText="先不操作"
        onCancel={() => setVoidOpen(false)}
        onOk={() => {
          if (!reason.trim()) {
            message.warning("请写下哪里坏了。");
            return Promise.reject(new Error("empty"));
          }
          return requestVoid(voidSerial.trim(), reason.trim())
            .then(() => {
              message.success("作废申请已交给主管。在通过之前，这个号还是待入库，不要拿去上架。");
              setVoidSerial("");
              setReason("");
              setVoidOpen(false);
              loadProgress();
            })
            .catch((error: unknown) => {
              message.error(error instanceof Error ? error.message : "这一步没有完成。");
              return Promise.reject(error instanceof Error ? error : new Error("fail"));
            });
        }}
      >
        <p>序列号 {voidSerial}</p>
        <Input.TextArea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="哪里坏了（污损、打歪、撕破）" />
      </Modal>
    </>
  );
}

function LabelCard({
  face,
  humanCode,
  internalCode,
  serial,
  gs1,
}: {
  face: "hangtag" | "peel";
  humanCode: string;
  internalCode: string;
  serial: string;
  gs1: string;
}): JSX.Element {
  if (face === "hangtag") {
    return (
      <div className="hangtag-card">
        <div className="hangtag-card__hole" />
        <div>SKU: {humanCode}</div>
        <div>Code: {internalCode}</div>
        <div>No. {serial}</div>
        <div className="hangtag-card__barcode-area">
          <BarcodeSvg value={gs1} displayValue={false} />
          <div className="hangtag-card__hri">{gs1}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="peel-label">
      <div className="peel-label__sku">SKU: {humanCode}</div>
      <div>Code: {internalCode}</div>
      <div>No. {serial}</div>
      <div>
        <BarcodeSvg value={gs1} height={28} displayValue={false} />
        <div className="peel-label__barcode">{gs1}</div>
      </div>
    </div>
  );
}

function LegacyTasks({
  tasks,
  selectedId,
  task,
  onSelect,
  onActivated,
  reload,
}: {
  tasks: PrintTask[];
  selectedId: string | null;
  task: PrintTask | null;
  onSelect: (id: string) => void;
  onActivated: () => void;
  reload: () => void;
}): JSX.Element {
  const { message } = App.useApp();
  const totals = countUnits(task);
  return (
    <>
      <Select
        style={{ width: 420, marginBottom: 12 }}
        placeholder="打开一个更早的单行任务"
        value={selectedId ?? undefined}
        onChange={onSelect}
        options={tasks.map((item) => ({
          value: item.id,
          label: `${item.taskNo} ${item.humanCode} ${item.serialFrom}-${item.serialTo}`,
        }))}
      />
      {task ? (
        <>
          <Space wrap>
            <Button onClick={() => downloadTask(task, "hangtag", message, reload)}>下载吊牌 PDF</Button>
            <Button onClick={() => downloadTask(task, "peel", message, reload)}>下载外标 PDF</Button>
          </Space>
          <div style={{ marginTop: 12 }}>
            <ScanBox
              placeholder="扫描这双的外标或吊牌"
              onSubmit={async (code) => {
                const result = await submitTaskScan(task.id, code);
                if (!result.ok) {
                  message.error(result.message);
                  return;
                }
                message.success(result.message);
                onActivated();
              }}
            />
          </div>
          <p>
            打印 {totals.printed}，已入库 {totals.activated}，已作废 {totals.voided}，待入库 {totals.remaining}
          </p>
          <Button
            disabled={totals.remaining > 0 || task.status === "CLOSED"}
            onClick={() => {
              void closePrintTask(task.id)
                .then(() => {
                  message.success("这个任务已收工。");
                  reload();
                  onActivated();
                })
                .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这一步没有完成。"));
            }}
          >
            确认三数对齐并结束任务
          </Button>
        </>
      ) : null}
    </>
  );
}

function countUnits(task: PrintTask | null): { printed: number; activated: number; voided: number; remaining: number } {
  if (!task) return { printed: 0, activated: 0, voided: 0, remaining: 0 };
  let activated = 0;
  let voided = 0;
  let remaining = 0;
  for (const unit of task.units) {
    if (unit.status === "VOIDED") voided += 1;
    else if (unit.status === "PRINTED") remaining += 1;
    else activated += 1;
  }
  return { printed: task.units.length, activated, voided, remaining };
}

function downloadArrival(
  arrivalNo: string,
  form: "hangtag" | "peel",
  notify: { success: (text: string) => void; error: (text: string) => void },
  reload: () => void
): void {
  const label = form === "hangtag" ? "吊牌" : "外标";
  void downloadFile(`/api/arrivals/${encodeURIComponent(arrivalNo)}/pdf?form=${form}`, `${arrivalNo}-${label}.pdf`)
    .then(() => {
      notify.success(`${label} PDF 已下载。拿到标签机上再打。`);
      reload();
    })
    .catch((error: unknown) => notify.error(error instanceof Error ? error.message : "这一步没有完成。"));
}

function downloadTask(
  task: PrintTask,
  form: "hangtag" | "peel",
  notify: { success: (text: string) => void; error: (text: string) => void },
  reload: () => void
): void {
  const label = form === "hangtag" ? "吊牌" : "外标";
  void downloadFile(`/api/print-tasks/${task.id}/pdf?form=${form}`, `${task.taskNo}-${label}.pdf`)
    .then(() => {
      notify.success(`${label} PDF 已下载。拿到标签机上再打。`);
      reload();
    })
    .catch((error: unknown) => notify.error(error instanceof Error ? error.message : "这一步没有完成。"));
}

function templateLabel(value: string): string {
  if (value === "hangtag") return "吊牌";
  if (value === "peel") return "外标";
  if (value === "location") return "库位码";
  if (value.includes("hangtag")) return "吊牌 / 外标";
  return value;
}
